import { RouteProp } from '@react-navigation/native';
import React, { useState } from 'react';
import { ProfilesTabParamList, useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useNotes from '../../../../../hooks/api/useNotes';
import NoteDetails from '../../../../shared/Notes/NoteDetails';
import NoteLinkPill from '../../../../shared/Notes/NoteDetails/NoteLinkPill';
import { getIntl } from '../../../../../utils/intl';
import useHasNotePermission from '../../../../../hooks/api/useHasNotePermission';
import useMedicalProfiles from '../../../../../hooks/api/useMedicalProfiles';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { useMaybeRenderedSideDrawerToggle } from '../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import SendNoteSideDrawerContent from '../../../../shared/SendNoteSideDrawerContent';
import useOrganization, { isRoleAdminOrOwner } from '../../../../../hooks/api/useOrganization';
import {
    getCurrentOrganizationClientRecordForProfile,
    useOrganizationClientRecordHistory,
} from '../../../../../hooks/api/useOrganizationClientRecords';
import { StandardPrompt } from '../../../../v2BaseComponents/Prompt';
import Text from '../../../../v2BaseComponents/Text';

const intl = getIntl('notes-details', {
    transferNote: 'Move Note',
    delete: 'Delete Note',
    copy: 'Copy to Another Profile',
    shareNote: 'Share Note',
    share: 'Share',
    saveToClientRecord: 'Save to Client Record',
    saveToClientRecordPromptMessage:
        'If this client disconnects your organization, a copy of this note will be retained on their client record.',
    confirmSaveToClientRecord: 'Save',
    cancel: 'Cancel',
});

export type NoteScreenOwnProps = {};

export type NoteScreenProps = NoteScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'note'>;
};

const NoteScreenScreen: React.FC<NoteScreenProps> = (props) => {
    const goBack = useGoBack();
    const navigator = useProfilesTabNavigation();
    const [isSaveToClientRecordPromptOpen, setIsSaveToClientRecordPromptOpen] = useState(false);
    const { isLoadingNote, note, deleteNote, isDeletingNote, linkNoteToClientRecord, isLinkingNoteToClientRecord } =
        useNotes({
            noteId: props.route.params.noteId,
            onNoteLoadFailure: () => {
                goBack();
            },
            onNoteLinkToClientRecordSuccess: () => {
                setIsSaveToClientRecordPromptOpen(false);
            },
        });
    // This screen is shared with the consumer app, where the user has no organization.
    const { myOrganization } = useOrganization({});
    const organizationId = myOrganization?.organization.organizationId ?? null;
    // Only owners and admins can see client records, and the client-record lookup below is owner/admin only.
    const canOpenClientRecords = !!organizationId && !!myOrganization?.role && isRoleAdminOrOwner(myOrganization.role);
    // Which client record the organization currently holds for this profile; a copy on a past record does not
    // count as saved, so the record is resolved rather than matched by organization alone.
    const { clientRecordHistory, isLoadingClientRecordHistory } = useOrganizationClientRecordHistory({
        organizationId: canOpenClientRecords ? organizationId : null,
        medicalProfileId: canOpenClientRecords ? note?.medicalProfileId ?? null : null,
    });
    const sendNoteSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'sendProfileNote',
        title: intl('shareNote'),
        renderContent: (onClose) => (
            <SendNoteSideDrawerContent
                noteId={props.route.params.noteId}
                onClose={onClose}
                medicalProfileId={props.route.params.profileId}
            />
        ),
        mobileOnFocus: () =>
            note
                ? navigator.navigate('send-note', {
                      noteId: props.route.params.noteId,
                      profileId: props.route.params.profileId,
                      noteEntityType: note?.entityType,
                  })
                : undefined,
    });

    const { medicalProfiles } = useMedicalProfiles({});
    const hasEnoughProfilesForCopy = medicalProfiles?.length > 1;

    const hasNoteEditPermission = useHasNotePermission({
        note: note,
        action: 'edit',
    });

    const canCopyNote =
        hasEnoughProfilesForCopy &&
        hasNoteEditPermission &&
        !note?.isSystemGenerated &&
        !isDeletingNote &&
        !!note?.medicalProfileId; // it shouldn't be a user note on this screen, but just in case

    // An organization member can keep a copy of a profile note on the client's record; the action goes away once
    // the organization's current record for the profile already holds one.
    const currentClientRecordId = note?.medicalProfileId
        ? getCurrentOrganizationClientRecordForProfile(clientRecordHistory, note.medicalProfileId)
              ?.organizationClientRecordId ?? null
        : null;
    const isSavedToClientRecord =
        !!currentClientRecordId &&
        !!note?.mirroredTo?.some((target) => target.organizationClientRecordId === currentClientRecordId);
    const canSaveToClientRecord =
        canOpenClientRecords &&
        !!note?.medicalProfileId &&
        !isLoadingClientRecordHistory &&
        !!currentClientRecordId &&
        !isSavedToClientRecord &&
        !isDeletingNote;
    // The copy the reader's organization holds of this note, reported only to members acting for an organization on
    // the profile; owners and admins can open it, since only they can open client records.
    const savedCopy = note?.mirroredTo?.[0] ?? null;

    return (
        <>
            <AnimateLoadedContent isLoading={isLoadingNote || !note?.medicalProfileId || isDeletingNote}>
                {() => (
                    <ProfileHeaderContainer profileId={note?.medicalProfileId || null} bottomBorder omitChatButton>
                        <NoteDetails
                            note={note!}
                            canEdit={!isDeletingNote}
                            linkPill={
                                savedCopy ? (
                                    <NoteLinkPill
                                        state="linked"
                                        onPress={
                                            canOpenClientRecords
                                                ? () =>
                                                      navigator.navigate('client-record-note', {
                                                          clientRecordId: savedCopy.organizationClientRecordId,
                                                          noteId: savedCopy.mirrorNoteId,
                                                      })
                                                : undefined
                                        }
                                        testID="note-link-pill-linked"
                                    />
                                ) : undefined
                            }
                            onEdit={(defaultTab) => {
                                navigator.navigate('edit-note', {
                                    noteId: note!.noteId,
                                    noteEntityType: note!.entityType,
                                    defaultTab,
                                    profileId: props.route.params.profileId,
                                });
                            }}
                            editActions={[
                                {
                                    icon: 'Send',
                                    title: intl('share'),
                                    onPress: () => {
                                        sendNoteSideDrawer.onOpen();
                                    },
                                    testID: 'send-note-button-action',
                                },
                                {
                                    icon: 'Move',
                                    title: intl('transferNote'),
                                    onPress: () => {
                                        navigator.navigate('transfer-note', {
                                            noteId: note!.noteId,
                                            noteEntityType: note!.entityType,
                                            profileId: props.route.params.profileId,
                                        });
                                    },
                                    show: hasNoteEditPermission,
                                    testID: 'transfer-note-button',
                                    displayInMoreMenu: true,
                                },
                                {
                                    icon: 'Copy',
                                    title: intl('copy'),
                                    onPress: () => {
                                        navigator.navigate('copy-note', {
                                            profileId: note!.medicalProfileId!,
                                            noteId: note!.noteId,
                                            noteEntityType: note!.entityType,
                                        });
                                    },
                                    isDisabled: isDeletingNote,
                                    show: canCopyNote,
                                    testID: 'copy-note-button',
                                    displayInMoreMenu: true,
                                },
                                {
                                    icon: 'Link',
                                    title: intl('saveToClientRecord'),
                                    onPress: () => {
                                        setIsSaveToClientRecordPromptOpen(true);
                                    },
                                    isDisabled: isLinkingNoteToClientRecord,
                                    show: canSaveToClientRecord,
                                    testID: 'save-note-to-client-record-button',
                                    displayInMoreMenu: true,
                                },
                                {
                                    icon: 'Trash2',
                                    title: intl('delete'),
                                    onPress: () => {
                                        deleteNote(props.route.params.noteId);
                                        goBack();
                                    },
                                    isDisabled: isDeletingNote,
                                    show: hasNoteEditPermission,
                                    testID: 'delete-note-button',
                                    displayInMoreMenu: true,
                                },
                            ]}
                        />
                    </ProfileHeaderContainer>
                )}
            </AnimateLoadedContent>
            {sendNoteSideDrawer.SideDrawer}
            <StandardPrompt
                isOpen={isSaveToClientRecordPromptOpen}
                setIsOpen={setIsSaveToClientRecordPromptOpen}
                title={intl('saveToClientRecord')}
                titleIcon="Link"
                testID="save-note-to-client-record-prompt"
                actions={[
                    {
                        title: intl('confirmSaveToClientRecord'),
                        variant: 'primary',
                        icon: 'Link',
                        onPress: () => linkNoteToClientRecord({ noteId: props.route.params.noteId }),
                        isDisabled: isLinkingNoteToClientRecord,
                        testID: 'confirm-save-note-to-client-record-button',
                    },
                    {
                        title: intl('cancel'),
                        variant: 'secondary',
                        icon: 'X',
                        onPress: () => setIsSaveToClientRecordPromptOpen(false),
                        isDisabled: isLinkingNoteToClientRecord,
                        testID: 'cancel-save-note-to-client-record-button',
                    },
                ]}
            >
                <Text variant="body-1">{intl('saveToClientRecordPromptMessage')}</Text>
            </StandardPrompt>
        </>
    );
};

export default React.memo(NoteScreenScreen);
